import { AfterViewInit, Component, ElementRef, Input, OnDestroy, QueryList, Inject, ViewChildren } from '@angular/core';
import { OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { WizTextInput } from '../wiz-text-input/wiz-text-input';
import { WizDatasource } from '../../../../services/collections/wiz-datasource';
import { WizBaseComponent } from '../../../core/wiz-base-component';
import { IWizTranslationEntry, VALUE_ENTER, VALUE_ESCAPE, VALUE_TAB, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizEventManager } from '../../../../events/wiz-event.manager';
import { WizEvent } from '../../../../events/wiz-event';

/**
 * Popup de saisie de libellés traductibles
 * Author : SVA
 */
@Component({
  selector: 'wiz-translation-pop',
  templateUrl: './wiz-translation-input.pop.html',
  styles: [':host {width: 100%}']
})
export class WizTranslationInputPop implements AfterViewInit, OnDestroy{


  /** Enfants text input */
  @ViewChildren(WizTextInput) tiChildren: QueryList<WizTextInput>;

  /** Text input qui a le focus */
  private _currentFocusedInput: WizTextInput;

  /** Collection construite à partir de la datasource,  limitée à 5 langues */
  public trimEntries: IWizTranslationEntry[];

  /** Datasource des entrées à afficher */
  private _entryDS: WizDatasource<IWizTranslationEntry>;
  public get entryDS(): WizDatasource<IWizTranslationEntry> {
    return this.getEntryDS();
  }
  public set entryDS(ds: WizDatasource<IWizTranslationEntry>) {
    this.setEntryDS(ds);
  }
  public getEntryDS(): WizDatasource<IWizTranslationEntry> {
    return this._entryDS ? this._entryDS : new WizDatasource<IWizTranslationEntry>();
  }
  public setEntryDS(ds: WizDatasource<IWizTranslationEntry>): void {
    this._entryDS = ds;
    this.trimEntries = this._entryDS.getWorkedSource().slice(0, this.maxTranslation);
  }
  
  /** Nombre max de drapeau affichés dans la pop */
  private _maxTranslation: number = 10;
  public set maxTranslation(maxT: number) {
    this.setMaxTranslation(maxT);
  }
  public get maxTranslation(): number {
    return this.getMaxTranslation();
  }
  public setMaxTranslation(maxT: number) {
    this._maxTranslation = maxT;
  }

  public getMaxTranslation(): number {
    return this._maxTranslation;
  }

  /**
   * Constructeur
   * @param elementRef pour pose d'écouteur
   * @param document pour écoute keydown
   * @param overlayRef pour fermer la pop
   */
  constructor(public elementRef: ElementRef,
              @Inject(DOCUMENT) protected document: Document, protected overlayRef?: OverlayRef) {
  }

  /** Composant parent */
  private _parentComponent: WizBaseComponent;
  public set parentComponent(parent: WizBaseComponent) {
    this.setParentComponent(parent);
  }
  public setParentComponent(parent: WizBaseComponent) {
    this._parentComponent = parent;
  }

  /** Nombre max de caractères */
  private _wizMaxChars: number;
  @Input()
  public set wizMaxChars(v: number) {
    this.setWizMaxChars(v);
  }
  public get wizMaxChars(): number {
    return this.getWizMaxChars();
  }
  /**
   * methodes getter/setter surchargeables du _wizMaxChars
   */
  protected setWizMaxChars(v: number) {
    this._wizMaxChars = v;
  }
  protected getWizMaxChars(): number {
    return this._wizMaxChars;
  }

  /**
   * Pose d'écouteurs
   */
  public ngAfterViewInit(): void {
    // Changement dans l'input
    WizEventManager.addListener(this, this.elementRef, WIZ_EVENT_TYPE.CHANGE, this.onLabelChange);
    // Tabulation intra pop up
    WizEventManager.addListener(this, this.elementRef, 'keydown', this.keyDownHandler);
    // Enregistrement de l'élément qui a le focus
    WizEventManager.addListener(this, this.elementRef, WIZ_EVENT_TYPE.FOCUS_IN, this._onFocusIn);
    this.initFields();
  }

  /**
   * Release d'écouteurs
   */
  public ngOnDestroy(): void {
    WizEventManager.removeListener(this, this.elementRef, WIZ_EVENT_TYPE.CHANGE);
    WizEventManager.removeListener(this, this.elementRef, 'keydown');
    WizEventManager.removeListener(this, this.elementRef, WIZ_EVENT_TYPE.FOCUS_IN);
  }

  /**
   * Map des entrées de traduction
   */
  private entryMap: Map<string, IWizTranslationEntry> = new Map();

  /**
   * Initialisation des champs
   */
  private initFields(): void {
    if (this._entryDS) {
      this.entryDS.getSource().reduce((map: Map<string, IWizTranslationEntry>, entry: IWizTranslationEntry) => {
        map.set(entry.languageCode, entry);
        return map;
      }, this.entryMap);
    }
  }

  /**
   * Changement dans un des inputs
   * @param e Evenement change
   */
  private onLabelChange(e: WizEvent): void {
    const ti: WizTextInput = e.component as WizTextInput;
    if (ti) {
      const entry: IWizTranslationEntry = this.entryMap.get(ti.id);
      if (entry) {
        entry.label = ti.wizValue;
        if (this._parentComponent && this._parentComponent.manageModel) {
          WizEventManager.dispatchEvent(this._parentComponent.elementRef, new WizEvent(WIZ_EVENT_TYPE.MODEL_UPDATE, this._parentComponent, entry));
        }
      }
    }
  }

  /**
   * Ecoute clavier pour gestion de la navigation et de la sélection d'horaires
   * @param e Evènement key down
   */
  private keyDownHandler(e: KeyboardEvent): void {
    if (e.key === VALUE_ESCAPE || e.key === VALUE_ENTER) { // Echap, entrée : fermeture
      if (this.overlayRef) {
        this.overlayRef.dispose();
      }
    } else if (e.key === VALUE_TAB) { // Le tab ne doit pas faire quitter la pop up
      if (this._currentFocusedInput === this.tiChildren.last && !e.shiftKey) {
        e.preventDefault();
        this.tiChildren.first.wizFocus();
      } else if (this._currentFocusedInput === this.tiChildren.first && e.shiftKey) {
        e.preventDefault();
        this.tiChildren.last.wizFocus();
      }
    }
  }

 /** Prise de focus > focus sur la 1ere trad */
 public wizFocus(): void {
  setTimeout(() => { // sinon les inputs ne sont pas encore créés
    if (this.tiChildren && this.tiChildren.first) {
      this.tiChildren.first.wizFocus();
    }
  }, 0);
 }

 /**
  * Handler de prise de focus pour stockage du composant qui prend ce focus
  * @param e Evenement focus in
  */
 private _onFocusIn(e: WizEvent): void {
  this._currentFocusedInput = e.component as WizTextInput;
 }

}
